import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';

import { RouterModule } from '@angular/router';
import { AlertManagerComponent } from './shared/components/alert-manager/alert-manager.component';
import { AuthService } from '@auth0/auth0-angular';
import {
    catchError,
    EMPTY,
    filter,
    interval,
    Subject,
    switchMap,
    take,
    takeUntil,
} from 'rxjs';
import { AuthFlowService } from './core/services/auth-wrapper/auth-flow.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from './shared/components/modal/modal.component';
import { UserAuthConstants } from './core/constants/user-auth-constants';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterModule, AlertManagerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private readonly authFlowService: AuthFlowService,
        private readonly auth0Service: AuthService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.startSessionValidationInterval();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Starts an interval that checks the user's session validity every 5 minutes.
     * This is crucial for ensuring that the user's session gets revoked if Auth0 determines
     * the session has expired, even if the user is still authenticated locally.
     *
     * An example scenario is when a user successfully resets their password on Auth0, but
     * the Auth0Service does not automatically log them out locally.
     *
     * This interval ensures that if Auth0 detects the session has expired,
     * it will trigger a logout flow to clear the local state and redirect the user to login again.
     */
    private startSessionValidationInterval(): void {
        interval(UserAuthConstants.AUTH0_SESSION_VALIDATION_INTERVAL)
            .pipe(
                /**
                 * First, verify the user is authenticated locally before making external Auth0 calls.
                 * This prevents unnecessary API calls when the user is already logged out.
                 */
                switchMap(() =>
                    this.auth0Service.isAuthenticated$.pipe(take(1)),
                ),
                filter((isAuthenticated) => isAuthenticated),
                switchMap(() =>
                    /**
                     * Perform a direct Auth0 session validation by attempting to retrieve
                     * an access token silently without cache. If Auth0 cannot return a valid
                     * token, it indicates the session has expired on Auth0's end.
                     */
                    this.auth0Service
                        .getAccessTokenSilently({
                            cacheMode: 'off',
                        })
                        .pipe(
                            catchError(() => {
                                /**
                                 * Auth0 failed to return a valid access token, indicating the session
                                 * has expired. Trigger logout to clear local state and redirect user.
                                 */
                                this.dialog.open(ModalComponent, {
                                    disableClose: true,
                                    data: {
                                        title: UserAuthConstants.SessionExpiredModalTitle,
                                        content:
                                            UserAuthConstants.SessionExpiredModalContent,
                                        primaryAction: {
                                            title: UserAuthConstants.SessionExpiredModalPrimaryAction,
                                            action: () => {
                                                this.authFlowService.logout();
                                            },
                                        },
                                        isShowCancel: false,
                                    },
                                });

                                return EMPTY;
                            }),
                        ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }
}
